import { useState } from "react"
import {
  Badge,
  Button,
  Grid,
  Group,
  Modal,
  Paper,
  Stack,
  Tabs,
  Text,
} from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { useTranslation } from "react-i18next"
import { useUser } from "../../user/hooks/useUser"
import { getErrorMessage } from "../utils"
import {
  useActivateAdminUser,
  useDeactivateAdminUser,
  useReset2fa,
} from "../hooks/useAdminUsers"
import type { AdminUser } from "../models/adminUser"
import {
  MAX_2FA_ATTEMPTS,
  STATUS_ACTIVE,
  STATUS_MAP,
  STATUS_SUSPENDED,
} from "../models/adminUser"
import ProfileInfoCard from "./ProfileInfoCard"
import ResetPasswordFormContent from "./ResetPasswordFormContent"
import RolesTabContent from "./RolesTabContent"
import SecurityTabContent from "./SecurityTabContent"

interface Props {
  user: AdminUser
}

export default function AdminUserDetailContent({ user }: Props) {
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageUsers ?? false

  const activateMutation = useActivateAdminUser()
  const deactivateMutation = useDeactivateAdminUser()
  const reset2faMutation = useReset2fa()

  const [resetPwOpened, { open: openResetPw, close: closeResetPw }] = useDisclosure(false)
  const [activeTab, setActiveTab] = useState<string | null>("account")

  const statusInfo = STATUS_MAP[user.status] ?? STATUS_MAP[0]
  const isActive = user.status === STATUS_ACTIVE

  const handleActivate = () => {
    modals.openConfirmModal({
      title: t("adminUsers.activate"),
      children: <Text size="sm">{t("adminUsers.activateConfirm")}</Text>,
      labels: { confirm: t("adminUsers.activate"), cancel: t("common.cancel") },
      confirmProps: { color: "green" },
      onConfirm: () => {
        activateMutation.mutate(user.id, {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("adminUsers.activateSuccess"),
            })
          },
          onError: (err) => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: getErrorMessage(err),
            })
          },
        })
      },
    })
  }

  const handleDeactivate = () => {
    modals.openConfirmModal({
      title: t("adminUsers.deactivate"),
      children: <Text size="sm">{t("adminUsers.deactivateConfirm")}</Text>,
      labels: { confirm: t("adminUsers.deactivate"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deactivateMutation.mutate(user.id, {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("adminUsers.deactivateSuccess"),
            })
          },
          onError: (err) => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: getErrorMessage(err),
            })
          },
        })
      },
    })
  }

  const handleReset2fa = () => {
    modals.openConfirmModal({
      title: t("adminUsers.reset2fa"),
      children: <Text size="sm">{t("adminUsers.reset2faConfirm")}</Text>,
      labels: { confirm: t("adminUsers.reset2fa"), cancel: t("common.cancel") },
      confirmProps: { color: "orange" },
      onConfirm: () => {
        reset2faMutation.mutate(user.id, {
          onSuccess: () => {
            notifications.show({
              color: "green",
              title: t("common.success"),
              message: t("adminUsers.reset2faSuccess"),
            })
          },
          onError: (err) => {
            notifications.show({
              color: "red",
              title: t("common.error"),
              message: getErrorMessage(err),
            })
          },
        })
      },
    })
  }

  return (
    <>
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="account" leftSection={<i className="fa fa-user" />}>
            {t("adminUsers.accountInfo")}
          </Tabs.Tab>
          <Tabs.Tab value="roles" leftSection={<i className="fa fa-shield" />}>
            {t("adminUsers.roles")}
          </Tabs.Tab>
          <Tabs.Tab value="security" leftSection={<i className="fa fa-lock" />}>
            {t("adminUsers.security")}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="account">
          <Grid>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <ProfileInfoCard
                user={user}
                canManage={canManage}
                onResetPassword={openResetPw}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Stack gap="md">
                <Paper
                  withBorder
                  p="md"
                  style={{
                    borderLeft: `4px solid var(--mantine-color-${statusInfo.color}-5)`,
                  }}
                >
                  <Text fw={600} size="sm" mb="md">
                    <i className="fa fa-user-circle" />
                    &nbsp;&nbsp;{t("adminUsers.accountStatus")}
                  </Text>
                  <Group gap="lg" wrap="nowrap" align="flex-start">
                    <div>
                      <Group gap="sm" mb="xs">
                        <i
                          className={statusInfo.icon}
                          style={{
                            fontSize: 28,
                            color: `var(--mantine-color-${statusInfo.color}-6)`,
                          }}
                        />
                        <Badge color={statusInfo.color} size="lg" variant="light">
                          {t(`adminUsers.${statusInfo.label}`)}
                        </Badge>
                      </Group>
                      <Text size="sm" c="dimmed">
                        {t(`adminUsers.${statusInfo.description}`)}
                      </Text>
                      {user.status === STATUS_SUSPENDED &&
                        user.failed_2fa_attempts >= MAX_2FA_ATTEMPTS && (
                          <Text size="sm" c="red" mt="xs">
                            <i className="fa fa-exclamation-triangle" />
                            &nbsp; Suspensão automática: {user.failed_2fa_attempts} tentativas falhadas.
                          </Text>
                        )}
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      {isActive ? (
                        <Button
                          color="red"
                          onClick={handleDeactivate}
                          loading={deactivateMutation.isPending}
                          leftSection={<i className="fa fa-times-circle" />}
                        >
                          {t("adminUsers.deactivate")}
                        </Button>
                      ) : (
                        <Button
                          color="green"
                          onClick={handleActivate}
                          loading={activateMutation.isPending}
                          leftSection={<i className="fa fa-check-circle" />}
                        >
                          {t("adminUsers.activate")}
                        </Button>
                      )}
                    </div>
                  </Group>
                </Paper>

                <Paper withBorder p="md">
                  <Text fw={600} size="sm" mb="md" tt="uppercase" lts="0.05em" c="dimmed">
                    <i className="fa fa-shield" />
                    &nbsp;&nbsp;{t("adminUsers.securityAccess")}
                  </Text>
                  <SecurityTabContent user={user} />
                </Paper>

                <Paper withBorder p="md">
                  <Text fw={600} size="sm" mb="md" tt="uppercase" lts="0.05em" c="dimmed">
                    <i className="fa fa-cog" />
                    &nbsp;&nbsp;{t("adminUsers.adminActions")}
                  </Text>
                  <Group gap="sm">
                    {canManage && (
                      <>
                        <Button
                          variant="light"
                          color="orange"
                          size="sm"
                          onClick={handleReset2fa}
                          loading={reset2faMutation.isPending}
                          leftSection={<i className="fa fa-mobile" />}
                        >
                          {t("adminUsers.reset2fa")}
                        </Button>
                      </>
                    )}
                  </Group>
                </Paper>
              </Stack>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="roles">
          <RolesTabContent userId={user.id} />
        </Tabs.Panel>

        <Tabs.Panel value="security">
          <Paper withBorder p="md">
            <Text fw={600} size="sm" mb="md" tt="uppercase" lts="0.05em" c="dimmed">
              <i className="fa fa-shield" />
              &nbsp;&nbsp;{t("adminUsers.securityDetails")}
            </Text>
            <SecurityTabContent user={user} />
          </Paper>
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={resetPwOpened}
        onClose={closeResetPw}
        title={t("adminUsers.resetPasswordTitle", { username: user.username })}
        centered
      >
        <ResetPasswordFormContent
          userId={user.id}
          username={user.username}
          onSuccess={closeResetPw}
        />
      </Modal>
    </>
  )
}
